import Dexie, { Table, Transaction } from 'dexie';
import { TabSong, Routine, PracticeSession, LyricSheet, LocalTabEdit, ScoreDraft } from '../types/index.js';
import { isUuid, newId } from '../utils/id.js';
import { rekeyTabReferencesIn } from './rekey.js';
import { normalizeEditPayload } from '../utils/tabEditPayload.js';
import { reportStorage } from '../stores/storageStore.js';

/** Id of the sample routine seeded by Dexie v1 builds (pre FRE-35). */
export const LEGACY_SAMPLE_ROUTINE_ID = 'routine-sample-1';

export interface LocalRoutine extends Routine {
  updatedAt: string; // ISO, bumped by every local write
  dirty: 0 | 1; // 1 = local change not yet pushed (numeric so it can be indexed)
  ownerId?: string; // server user id this copy belongs to; undefined = guest/unclaimed
  serverUpdatedAt?: string; // updatedAt of the last server copy pushed or pulled; set = exists on server
  deletedAt?: string; // tombstone, waiting for DELETE to reach the server
  legacy?: 0 | 1; // re-keyed from a pre-FRE-35 id; first PUT sends replaces_legacy
  isSample?: 0 | 1; // seeded sample routine
  pushRejectedAt?: string; // updatedAt the server rejected (400/409); quarantined until edited or retried
  pushRejectedStatus?: 400 | 409;
}

export interface LocalSetting {
  key: string;
  value: any;
}

export interface LocalPlaylist {
  id: string;
  ownerId?: string; // FRE-141: signed-in user id; undefined = guest scope
  title: string;
  createdAt: string;
  tabIds: string[];
  // FRE-143 sync metadata (Dexie v6). Optional in the type so older call sites compile; always written by operations.
  updatedAt: string; // bumped by every local write
  dirty: 0 | 1; // 1 = local change not pushed
  serverUpdatedAt?: string; // set = exists on server
  deletedAt?: string; // tombstone waiting for DELETE
  pushRejectedAt?: string; // = updatedAt when the server answered 400; skip until the next local edit
}

export interface LocalFavorite {
  tabId: string;
  addedAt: string;
}

/** FRE-141: per-account favorite. `ownerId` is '' for the guest scope (compound primary key [ownerId+tabId]). */
export interface LocalUserFavorite extends LocalFavorite {
  ownerId: string;
  // FRE-143 (Dexie v6): desired-state sync
  updatedAt: string;
  dirty: 0 | 1; // 1 = local state not pushed
  serverKnown: 0 | 1; // 1 = the server has (or had) this favorite
  removedAt?: string; // tombstone: unfavorited locally, DELETE not sent yet
}

/**
 * Dexie v1 -> v2 upgrade. Runs once per device inside the open() transaction, before any UI
 * holds an id. Never drops data: a record that fails to transform is logged and left as is.
 */
export async function upgradeToV2(tx: Transaction): Promise<void> {
  const nowIso = new Date().toISOString();
  const blockMap = new Map<string, string>();
  const ambiguous = new Set<string>();

  const routinesTable = tx.table('routines');
  const routines: Routine[] = await routinesTable.toArray();

  for (const r of routines) {
    try {
      const idIsUuid = isUuid(r.id);
      const newRoutineId = idIsUuid ? r.id : newId();
      const isOldSample = r.id === LEGACY_SAMPLE_ROUTINE_ID;

      const blocks = (r.blocks ?? []).map((b) => {
        if (isUuid(b.id)) return b;
        const nb = newId();
        if (blockMap.has(b.id)) ambiguous.add(b.id);
        blockMap.set(b.id, nb);
        return { ...b, id: nb };
      });

      const upgraded: LocalRoutine = {
        ...r,
        id: newRoutineId,
        blocks,
        updatedAt: r.createdAt ?? nowIso,
        dirty: isOldSample ? 0 : 1,
        legacy: idIsUuid ? 0 : 1,
        ...(isOldSample && { isSample: 1 as const })
      };

      if (newRoutineId !== r.id) {
        await routinesTable.delete(r.id);
      }
      await routinesTable.put(upgraded);
    } catch (err) {
      console.error('Dexie v2 upgrade: failed to transform routine', r?.id, err);
    }
  }

  // Sessions: re-key unsynced legacy session ids (FRE-43) and remap block ids.
  try {
    const sessionsTable = tx.table('sessions');
    const sessions: PracticeSession[] = await sessionsTable.toArray();
    for (const s of sessions) {
      try {
        const blocks = (s.blocks ?? []).map((b) => {
          const mapped = blockMap.get(b.blockId);
          return mapped && !ambiguous.has(b.blockId) ? { ...b, blockId: mapped } : b;
        });
        const needsRekey = s.synced === 0 && !isUuid(s.clientSessionId);
        if (needsRekey) {
          await sessionsTable.delete(s.clientSessionId);
          await sessionsTable.put({ ...s, clientSessionId: newId(), blocks });
        } else {
          await sessionsTable.put({ ...s, blocks });
        }
      } catch (err) {
        console.error('Dexie v2 upgrade: failed to transform session', s?.clientSessionId, err);
      }
    }
  } catch (err) {
    console.error('Dexie v2 upgrade: sessions step failed', err);
  }
}

/**
 * Dexie v2 -> v3 upgrade (FRE-108): sessions get an owner (`userId`, indexed). Existing unowned rows
 * belong to whoever is signed in (real account, not guest) at upgrade time; if nobody/guest, they stay
 * unowned (guest scope) and never sync for an account.
 */
export async function upgradeToV3(tx: Transaction): Promise<void> {
  let uid: string | undefined;
  try {
    const token = localStorage.getItem('fretboard_token');
    const isGuest = localStorage.getItem('fretboard_is_guest') === 'true';
    const raw = localStorage.getItem('fretboard_user');
    uid = token && !isGuest && raw ? (JSON.parse(raw)?.id as string | undefined) : undefined;
  } catch {
    uid = undefined;
  }
  if (!uid) return;
  try {
    await tx
      .table('sessions')
      .toCollection()
      .modify((s: PracticeSession) => {
        if (!s.userId) s.userId = uid;
      });
  } catch (err) {
    console.error('Dexie v3 upgrade: sessions owner step failed', err);
  }
}

/** Signed-in (non-guest) user id stored on this device, or undefined (guest / nobody). */
function storedAccountId(): string | undefined {
  try {
    const token = localStorage.getItem('fretboard_token');
    const isGuest = localStorage.getItem('fretboard_is_guest') === 'true';
    const raw = localStorage.getItem('fretboard_user');
    return token && !isGuest && raw ? (JSON.parse(raw)?.id as string | undefined) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Dexie v3 -> v4 upgrade (FRE-141): tabs, playlists, favorites and last_opened_tab become per-account.
 * Existing unowned rows go to whoever is signed in (real account) at upgrade time; if nobody/guest they
 * stay unowned = guest scope. Nothing is deleted: the legacy `favorites` table is kept (unused) after its
 * rows are copied into `userFavorites`.
 */
export async function upgradeToV4(tx: Transaction): Promise<void> {
  const uid = storedAccountId();
  try {
    if (uid) {
      await tx.table('tabs').toCollection().modify((t: TabSong) => {
        if (!t.ownerId) t.ownerId = uid;
      });
      await tx.table('playlists').toCollection().modify((p: LocalPlaylist) => {
        if (!p.ownerId) p.ownerId = uid;
      });
    }
  } catch (err) {
    console.error('Dexie v4 upgrade: tabs/playlists owner step failed', err);
  }
  try {
    const old: LocalFavorite[] = await tx.table('favorites').toArray();
    for (const f of old) {
      await tx.table('userFavorites').put({ ...f, ownerId: uid ?? '' });
    }
  } catch (err) {
    console.error('Dexie v4 upgrade: favorites step failed', err);
  }
  try {
    const settings = tx.table('settings');
    const legacy = await settings.get('last_opened_tab');
    if (legacy) {
      await settings.put({ key: `last_opened_tab:${uid ?? 'guest'}`, value: legacy.value ?? legacy });
      await settings.delete('last_opened_tab');
    }
  } catch (err) {
    console.error('Dexie v4 upgrade: settings step failed', err);
  }
}

/**
 * Dexie v4 -> v5 upgrade (FRE-142): legacy `tab-local-*` tabs get a fresh UUID, are queued for upload
 * (`pendingUpload: 1`), and every local reference (routine blocks, playlists, favorites, last-opened tab)
 * follows. `tab_alias:<old>` keeps old `/tabs/tab-local-...` links working. Each row has its own
 * try/catch: a failure is logged and the row is left as is (never drop data).
 */
export async function upgradeToV5(tx: Transaction): Promise<void> {
  let rows: TabSong[] = [];
  try {
    rows = (await tx.table('tabs').toArray()) as TabSong[];
  } catch (err) {
    console.error('Dexie v5 upgrade: reading tabs failed', err);
    return;
  }
  for (const row of rows) {
    if (typeof row.id !== 'string' || !row.id.startsWith('tab-local-')) continue;
    try {
      const nid = newId();
      await tx.table('tabs').delete(row.id);
      await tx.table('tabs').put({ ...row, id: nid, pendingUpload: 1, status: 'PRIVATE', fileUrl: '' });
      await rekeyTabReferencesIn((name) => tx.table(name), row.id, nid);
      await tx.table('settings').put({ key: 'tab_alias:' + row.id, value: nid });
    } catch (err) {
      console.error('Dexie v5 upgrade: failed to re-key tab', row.id, err);
    }
  }
}

/** Pre-FRE-143 favorite-toggle queue keys (localStorage), migrated into Dexie tombstones by v6. */
const LEGACY_FAV_QUEUE_PREFIX = 'fretboard:pending-favorite-toggles';

/**
 * Dexie v5 -> v6 upgrade (FRE-143): playlists get sync metadata (non-UUID `pl-...` ids are re-keyed to UUIDs;
 * routine-style: put new, delete old), favorites become desired-state rows, and the per-account localStorage
 * toggle queues become tombstones. Never drops data: failures are logged and the row is left as is.
 */
export async function upgradeToV6(tx: Transaction): Promise<void> {
  const nowIso = new Date().toISOString();
  try {
    const table = tx.table('playlists');
    const rows = (await table.toArray()) as Array<Partial<LocalPlaylist> & { id: string }>;
    for (const p of rows) {
      try {
        const id = isUuid(p.id) ? p.id : newId();
        if (id !== p.id) await table.delete(p.id);
        await table.put({ ...p, id, updatedAt: p.createdAt ?? nowIso, dirty: 1 });
      } catch (err) {
        console.error('Dexie v6 upgrade: failed to transform playlist', p?.id, err);
      }
    }
  } catch (err) {
    console.error('Dexie v6 upgrade: playlists step failed', err);
  }
  try {
    const table = tx.table('userFavorites');
    const rows = (await table.toArray()) as Array<Partial<LocalUserFavorite> & { ownerId: string; tabId: string }>;
    for (const f of rows) {
      try {
        await table.put({ ...f, updatedAt: f.addedAt ?? nowIso, dirty: 1, serverKnown: 0 });
      } catch (err) {
        console.error('Dexie v6 upgrade: failed to transform favorite', f?.tabId, err);
      }
    }
    // Migrate per-account queues: a queued id without a row means "unfavorited offline".
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LEGACY_FAV_QUEUE_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      try {
        const uid = k.slice(LEGACY_FAV_QUEUE_PREFIX.length + 1); // after ':'
        if (k.length > LEGACY_FAV_QUEUE_PREFIX.length && uid) {
          const parsed = JSON.parse(localStorage.getItem(k) ?? '[]');
          if (Array.isArray(parsed)) {
            for (const tabId of parsed) {
              if (typeof tabId !== 'string') continue;
              if (await table.get([uid, tabId])) continue;
              await table.put({
                ownerId: uid,
                tabId,
                addedAt: nowIso,
                updatedAt: nowIso,
                removedAt: nowIso,
                dirty: 1,
                serverKnown: 1
              });
            }
          }
        }
        localStorage.removeItem(k); // also the unscoped legacy key: owner unknown, not replayed
      } catch (err) {
        console.error('Dexie v6 upgrade: favorite queue step failed', k, err);
      }
    }
  } catch (err) {
    console.error('Dexie v6 upgrade: favorites step failed', err);
  }
  // References to the re-keyed playlist ids: none exist in other tables (playlists are referenced by id nowhere).
}

/**
 * Dexie v9 -> v10 upgrade (FRE-224): pendingDifficulty rows are converted into queued DIFFICULTY edits
 * in tabEdits (Dexie v10). Local-only tabs have pendingDifficulty reset to 0 (values stay; upload carries them).
 * Server tabs get exactly one QUEUED DIFFICULTY edit and pendingDifficulty = 0.
 * Never drops data: individual try/catch per row.
 */
export async function upgradeToV10(tx: Transaction): Promise<void> {
  const nowIso = new Date().toISOString();
  try {
    const tabsTable = tx.table('tabs');
    const tabEditsTable = tx.table('tabEdits');
    const rows = (await tabsTable.toArray()) as TabSong[];

    for (const row of rows) {
      if (Number(row.pendingDifficulty) !== 1) continue;
      try {
        const isLocalOnly =
          row.pendingUpload === 1 ||
          (row.status === 'PRIVATE' && !row.fileUrl) ||
          !row.ownerId;

        if (isLocalOnly) {
          await tabsTable.update(row.id, { pendingDifficulty: 0 });
        } else {
          await tabEditsTable.put({
            id: newId(),
            tabId: row.id,
            ownerId: row.ownerId!,
            kind: 'DIFFICULTY',
            payload: {
              rhythm_difficulty: row.rhythmDifficulty ?? null,
              lead_difficulty: row.leadDifficulty ?? null
            },
            baseRevision: row.revision ?? 1,
            status: 'QUEUED',
            mode: 'suggest',
            origin: 'migrated',
            attempts: 0,
            createdAt: nowIso,
            updatedAt: nowIso
          });
          await tabsTable.update(row.id, { pendingDifficulty: 0 });
        }
      } catch (err) {
        console.error('Dexie v10 upgrade: failed to migrate row', row?.id, err);
      }
    }
  } catch (err) {
    console.error('Dexie v10 upgrade: tabs step failed', err);
  }
}

/**
 * Dexie v10 -> v11 upgrade (FRE-224): rewrites legacy queued LYRICS payloads `{ lyrics }` to `{ content }`
 * and SCORE `engine: "alphaTab@x.y.z"` to `"x.y.z"` so already-queued edits pass API validation.
 */
export async function upgradeToV11(tx: Transaction): Promise<void> {
  try {
    await tx
      .table('tabEdits')
      .toCollection()
      .modify((row: { kind: string; payload: unknown }) => {
        if (row.kind === 'LYRICS' || row.kind === 'SCORE') {
          row.payload = normalizeEditPayload(row.kind, row.payload);
        }
      });
  } catch (err) {
    console.error('Dexie v11 upgrade: tabEdits payload step failed', err);
  }
}

export class FretBoardDatabase extends Dexie {
  tabs!: Table<TabSong, string>;
  routines!: Table<LocalRoutine, string>;
  sessions!: Table<PracticeSession, string>;
  playlists!: Table<LocalPlaylist, string>;
  favorites!: Table<LocalFavorite, string>; // legacy (pre FRE-141), no longer read
  userFavorites!: Table<LocalUserFavorite, [string, string]>;
  settings!: Table<LocalSetting, string>;
  lyricSheets!: Table<LyricSheet, string>;
  tabEdits!: Table<LocalTabEdit, string>;
  scoreDrafts!: Table<ScoreDraft, [string, string]>;

  constructor() {
    super('FretBoardLocalDB');

    this.version(1).stores({
      tabs: 'id, title, artist, tuning, status, isDownloaded',
      routines: 'id, title, targetDurationMinutes, createdAt',
      sessions: 'clientSessionId, totalDurationSeconds, synced, createdAt',
      playlists: 'id, title, createdAt',
      favorites: 'tabId, addedAt',
      settings: 'key'
    });

    // v2 (FRE-35 / FRE-43): routine sync metadata, UUID re-keying. Only changed tables are listed.
    this.version(2)
      .stores({
        routines: 'id, title, targetDurationMinutes, createdAt, dirty, ownerId'
      })
      .upgrade(upgradeToV2);

    // v3 (FRE-108): session owner index.
    this.version(3)
      .stores({
        sessions: 'clientSessionId, totalDurationSeconds, synced, createdAt, userId'
      })
      .upgrade(upgradeToV3);

    // v4 (FRE-141): per-account tabs / playlists / favorites / last_opened_tab.
    this.version(4)
      .stores({
        tabs: 'id, title, artist, tuning, status, isDownloaded, ownerId',
        playlists: 'id, title, createdAt, ownerId',
        userFavorites: '[ownerId+tabId], ownerId, addedAt'
      })
      .upgrade(upgradeToV4);

    // v5 (FRE-142): upload queue index; legacy tab-local-* ids re-keyed to UUIDs.
    this.version(5)
      .stores({
        tabs: 'id, title, artist, tuning, status, isDownloaded, ownerId, pendingUpload'
      })
      .upgrade(upgradeToV5);

    // v6 (FRE-143): playlist + favorite sync metadata, UUID playlist ids.
    this.version(6)
      .stores({
        playlists: 'id, title, createdAt, ownerId, dirty',
        userFavorites: '[ownerId+tabId], ownerId, addedAt, dirty'
      })
      .upgrade(upgradeToV6);

    // v7 (FRE-188): offline difficulty edit queue. No upgrade function: the new fields start undefined.
    this.version(7).stores({
      tabs: 'id, title, artist, tuning, status, isDownloaded, ownerId, pendingUpload, pendingDifficulty'
    });

    // v8: chord lyric sheets (local only for now). New table, no upgrade function.
    this.version(8).stores({
      lyricSheets: 'id, tabId, ownerId, updatedAt'
    });

    // v9 (FRE-211): lyric sheet sync. Sheets written before sync existed are marked dirty so they get pushed.
    this.version(9)
      .stores({
        lyricSheets: 'id, tabId, ownerId, updatedAt, dirty'
      })
      .upgrade(async (tx) => {
        try {
          await tx.table('lyricSheets').toCollection().modify((r: LyricSheet) => {
            r.dirty = 1;
          });
        } catch (err) {
          console.error('Dexie v9 upgrade: lyric sheets step failed', err);
        }
      });

    // v10 (FRE-224): tab edit suggestions, offline edits queue, score drafts.
    this.version(10)
      .stores({
        tabEdits: 'id, tabId, ownerId, status, [ownerId+status], updatedAt',
        scoreDrafts: '[ownerId+tabId], ownerId, updatedAt'
      })
      .upgrade(upgradeToV10);

    // v11 (FRE-224): payload field fixes for queued edits (no schema change).
    this.version(11).stores({}).upgrade(upgradeToV11);
  }
}

export const db = new FretBoardDatabase();

// FRE-171: surface IndexedDB failures (private mode, blocked upgrade, quota) in the app shell.
db.open().catch((e) => reportStorage('unavailable', e));
db.on('blocked', () => reportStorage('blocked'));
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason as { name?: string; inner?: { name?: string } } | undefined;
    if (reason?.name === 'QuotaExceededError' || reason?.inner?.name === 'QuotaExceededError') {
      reportStorage('quota', reason);
    }
  });
}
